import { doc, getDoc, GeoPoint, Timestamp } from "firebase/firestore";
import type { ScheduleLabels } from "../i18n/types";
import { getFirebase } from "../firebase/config";

/** iOS `slugify` ile aynı mantık: Türkçe harfleri ASCII’ye (ı→i vb.). */
function foldTurkishCharsToAscii(s: string): string {
  const map: Record<string, string> = {
    ğ: "g",
    Ğ: "g",
    ü: "u",
    Ü: "u",
    ş: "s",
    Ş: "s",
    ı: "i",
    İ: "i",
    ö: "o",
    Ö: "o",
    ç: "c",
    Ç: "c",
  };
  return [...s.normalize("NFC")].map((ch) => map[ch] ?? ch).join("");
}

/** any-ascii ile: URL’de kalan Arapça/Farsça/Hintçe… → Latin slug (iOS ICU’ya yakın; ayrı chunk). */
async function approximateLatinSlugFromPathSegment(s: string): Promise<string> {
  const { default: anyAscii } = await import("any-ascii");
  const t = foldTurkishCharsToAscii(s.normalize("NFC").trim());
  const a = anyAscii(t);
  return a
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}


/** iOS / Firestore `weddings/{slug}` ile uyumlu geniş model (web’de ihtiyaç duyulan alanlar). */
export type WeddingDoc = {
  slug: string;
  brideName: string;
  groomName: string;
  eventTitle: string;
  backgroundUrl?: string;
  description?: string | null;
  date?: Timestamp;
  /** iOS: kullanıcı tarih seçmediyse false (geri sayım gösterilmez). */
  hasSelectedDate?: boolean;
  /** iOS: `showEndDate && !isAllDay` iken dolu. */
  endDate?: Timestamp | null;
  isAllDay?: boolean;
  venueName?: string;
  venueAddress?: string;
  venueCoords?: GeoPoint;
  rsvpEnabled?: boolean;
  guestLimit?: number | null;
  rsvpDeadline?: Timestamp | null;
  mealEnabled?: boolean;
  mealOptions?: Record<string, boolean> | null;
  hostMealNote?: string | null;
  timelineEnabled?: boolean;
  timelineItems?: Array<{ label: string; time?: Timestamp }>;
  /** Pro — `weddingInvitePublic`; web’de şarkı isteği kartı. */
  isSongRequestEnabled?: boolean;
};

/** Misafir tarafı — `userId` içermez; `weddingInvitePublic/{slug}` (iOS kayıtta senkron). */
export async function loadWeddingBySlug(slug: string): Promise<WeddingDoc | null> {
  const { db } = getFirebase();
  const raw = slug.normalize("NFC").trim();
  if (!raw) return null;
  const turkish = foldTurkishCharsToAscii(raw);
  const quickKeys = [...new Set([raw, turkish].filter((k) => k.length > 0))];

  for (const key of quickKeys) {
    const ref = doc(db, "weddingInvitePublic", key);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      return { slug: key, ...snap.data() } as WeddingDoc;
    }
  }

  const latin = await approximateLatinSlugFromPathSegment(raw);
  if (latin.length > 0 && !quickKeys.includes(latin)) {
    const ref = doc(db, "weddingInvitePublic", latin);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      return { slug: latin, ...snap.data() } as WeddingDoc;
    }
  }
  return null;
}

export function formatWeddingDate(doc: WeddingDoc, intlLocale: string, dateTBD: string): string {
  const ts = doc.date;
  if (!ts || !(ts instanceof Timestamp)) return dateTBD;
  const d = ts.toDate();
  const df = new Intl.DateTimeFormat(intlLocale, {
    day: "numeric",
    month: "short",
    year: "2-digit",
    ...(doc.isAllDay ? {} : { hour: "numeric", minute: "2-digit" }),
  });
  return df.format(d);
}

/** Kart footer için: başlangıç; varsa bitiş (iOS `endDate`, yalnızca gün boyu değilken). */
export function formatWeddingScheduleLines(
  doc: WeddingDoc,
  intlLocale: string,
  labels: ScheduleLabels,
): string[] {
  const startTs = doc.date;
  if (!startTs || !(startTs instanceof Timestamp)) return [labels.dateTBD];

  const start = startTs.toDate();
  const allDay = doc.isAllDay === true;

  const dateOnly = new Intl.DateTimeFormat(intlLocale, {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
  const dateTime = new Intl.DateTimeFormat(intlLocale, {
    day: "numeric",
    month: "short",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
  const timeOnly = new Intl.DateTimeFormat(intlLocale, {
    hour: "numeric",
    minute: "2-digit",
  });

  if (allDay) {
    return [dateOnly.format(start)];
  }

  const endRaw = doc.endDate;
  const endTs = endRaw instanceof Timestamp ? endRaw : null;
  if (!endTs) {
    return [dateTime.format(start)];
  }

  const end = endTs.toDate();
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  if (sameDay) {
    const dStr = dateOnly.format(start);
    return [`${dStr} · ${timeOnly.format(start)} – ${timeOnly.format(end)}`];
  }

  return [`${labels.scheduleStarts} ${dateTime.format(start)}`, `${labels.scheduleEnds} ${dateTime.format(end)}`];
}

function coerceTimestamp(v: unknown): Timestamp | null {
  if (v instanceof Timestamp) return v;
  if (v != null && typeof v === "object" && "seconds" in v && typeof (v as { seconds: unknown }).seconds === "number") {
    const o = v as { seconds: number; nanoseconds?: number };
    return new Timestamp(o.seconds, typeof o.nanoseconds === "number" ? o.nanoseconds : 0);
  }
  return null;
}

/** `timelineEnabled` ve geçerli `time` + `label` olan öğeler; zamana göre sıralı. */
export function getNormalizedTimelineItems(doc: WeddingDoc): { label: string; at: Date }[] {
  if (doc.timelineEnabled !== true || !Array.isArray(doc.timelineItems)) return [];
  const out: { label: string; at: Date }[] = [];
  for (const raw of doc.timelineItems) {
    if (!raw || typeof raw !== "object") continue;
    const labelRaw = (raw as { label?: unknown }).label;
    const label = typeof labelRaw === "string" ? labelRaw.trim() : "";
    if (!label) continue;
    const ts = coerceTimestamp((raw as { time?: unknown }).time);
    if (!ts) continue;
    out.push({ label, at: ts.toDate() });
  }
  out.sort((a, b) => a.at.getTime() - b.at.getTime());
  return out;
}

/** Program satırı: düğün günüyle aynı günse yalnızca saat, aksi veya all-day ise kısa tarih + saat. */
export function formatTimelineItemTime(at: Date, doc: WeddingDoc, intlLocale: string): string {
  const startTs = doc.date;
  const weddingStart = startTs instanceof Timestamp ? startTs.toDate() : null;
  const allDay = doc.isAllDay === true;
  const timeOnly = new Intl.DateTimeFormat(intlLocale, { hour: "numeric", minute: "2-digit" });
  const dateTime = new Intl.DateTimeFormat(intlLocale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  if (!weddingStart || allDay) {
    return dateTime.format(at);
  }
  const sameDay =
    at.getFullYear() === weddingStart.getFullYear() &&
    at.getMonth() === weddingStart.getMonth() &&
    at.getDate() === weddingStart.getDate();
  return sameDay ? timeOnly.format(at) : dateTime.format(at);
}

/** Düğün başlangıcı için epoch ms; tarih yoksa veya seçilmediyse `null`. */
export function getWeddingCountdownTargetMs(w: WeddingDoc): number | null {
  if (w.hasSelectedDate === false) return null;
  const startTs = w.date;
  if (!startTs || !(startTs instanceof Timestamp)) return null;
  return startTs.toDate().getTime();
}

/** RSVP son günü (takvim günü, yerel saat) dolmuşsa true — form kapatılır. */
export function isRsvpDeadlinePassed(doc: WeddingDoc): boolean {
  const dl = coerceTimestamp(doc.rsvpDeadline);
  if (!dl) return false;
  const end = dl.toDate();
  end.setHours(23, 59, 59, 999);
  return Date.now() > end.getTime();
}

/** RSVP son günü takvim tarihi (örn. `Apr 10, 26`); deadline yoksa `null`. */
export function formatRsvpDeadlineForDisplay(doc: WeddingDoc, intlLocale: string): string | null {
  const dl = coerceTimestamp(doc.rsvpDeadline);
  if (!dl) return null;
  const d = dl.toDate();
  return new Intl.DateTimeFormat(intlLocale, {
    day: "numeric",
    month: "short",
    year: "2-digit",
  }).format(d);
}

/** Firestore GeoPoint veya eski/serileştirilmiş `{ latitude, longitude }` için. */
export function getVenueLatLng(w: WeddingDoc): { lat: number; lng: number } | null {
  const c = w.venueCoords;
  if (!c) return null;
  if (c instanceof GeoPoint) return { lat: c.latitude, lng: c.longitude };
  const o = c as unknown as { latitude?: number; longitude?: number };
  if (typeof o.latitude === "number" && typeof o.longitude === "number") {
    return { lat: o.latitude, lng: o.longitude };
  }
  return null;
}

const MEAL_OPTION_ORDER = ["vegetarian", "vegan", "glutenFree", "nutAllergy"] as const;

export type MealOptionKey = (typeof MEAL_OPTION_ORDER)[number];

/** Ev sahibinin RSVP’te gösterdiği seçenekler (`mealOptions` içinde `true` olanlar, sabit sırada). */
export function getActiveMealOptionKeys(w: WeddingDoc): MealOptionKey[] {
  if (w.mealEnabled !== true || !w.mealOptions || typeof w.mealOptions !== "object") return [];
  const mo = w.mealOptions;
  return MEAL_OPTION_ORDER.filter((k) => mo[k] === true);
}

